import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ============================================
// 用户类型定义
// ============================================
export interface User {
	id: string
	username: string
	email: string
	avatar?: string
	role: 'admin' | 'manager' | 'operator'
	roleName: string
	permissions: string[]
	is_super_admin?: boolean
	crmeb_uid?: number | null
}

// ============================================
// 侧边栏状态
// ============================================
interface SidebarState {
	collapsed: boolean
	mobileOpen: boolean
	setCollapsed: (collapsed: boolean) => void
	setMobileOpen: (open: boolean) => void
	toggleCollapsed: () => void
	toggleMobile: () => void
}

// ============================================
// 用户状态
// ============================================
interface UserState {
	user: User | null
	setUser: (user: User) => void
	logout: () => void
	updateUser: (user: Partial<User>) => void
}

// ============================================
// 全局应用状态
// ============================================
interface AppState {
	// 侧边栏
	sidebar: SidebarState

	// 用户
	user: UserState

	// 全局加载状态
	globalLoading: boolean
	setGlobalLoading: (loading: boolean) => void
}

// ============================================
// 创建 Store
// ============================================
export const useAppStore = create<AppState>()(
	persist(
		(set) => ({
			// 侧边栏状态
			sidebar: {
				collapsed: false,
				mobileOpen: false,
				setCollapsed: (collapsed) => set((state) => ({
					sidebar: { ...state.sidebar, collapsed }
				})),
				setMobileOpen: (mobileOpen) => set((state) => ({
					sidebar: { ...state.sidebar, mobileOpen }
				})),
				toggleCollapsed: () => set((state) => ({
					sidebar: { ...state.sidebar, collapsed: !state.sidebar.collapsed }
				})),
				toggleMobile: () => set((state) => ({
					sidebar: { ...state.sidebar, mobileOpen: !state.sidebar.mobileOpen }
				})),
			},

			// 用户状态（不持久化）
			user: {
				user: null,
				setUser: (user) => set((state) => ({
					user: { ...state.user, user }
				})),
				logout: () => set((state) => ({
					user: { ...state.user, user: null }
				})),
				updateUser: (userData) => set((state) => ({
					user: {
						...state.user,
						user: state.user.user ? { ...state.user.user, ...userData } : null
					}
				})),
			},

			// 全局加载
			globalLoading: false,
			setGlobalLoading: (globalLoading) => set({ globalLoading }),
		}),
		{
			name: 'carrot-admin-store',
			version: 1, // 版本号变更会清除旧数据
			partialize: (state) => ({
				// 只持久化 sidebar 状态，不持久化用户状态
				sidebar: {
					collapsed: state.sidebar.collapsed,
				},
			}),
		}
	)
)

// ============================================
// 选择器 Hooks（优化性能）
// ============================================
export const useSidebar = () => useAppStore((state) => state.sidebar)
export const useUser = () => useAppStore((state) => state.user)
export const useGlobalLoading = () => useAppStore((state) => state.globalLoading)

// ============================================
// 模拟当前登录用户
// ============================================
export const mockCurrentUser: User = {
	id: '1',
	username: 'admin',
	email: 'admin@carrot.com',
	role: 'admin',
	roleName: '超级管理员',
	permissions: ['user:read', 'user:write', 'order:read', 'order:write', 'product:read', 'product:write', 'supplier:read'],
}
